import React from 'react';

interface SkeletonLoaderProps {
  type?: 'card' | 'text' | 'image' | 'avatar' | 'button' | 'list' | 'table' | 'form';
  className?: string;
  lines?: number;
  width?: string;
  height?: string;
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  type = 'text',
  className = '',
  lines = 1,
  width,
  height
}) => {
  const getSkeletonClass = () => {
    const baseClass = 'bg-gray-200 dark:bg-gray-700 rounded animate-pulse';
    
    switch (type) {
      case 'card':
        return `${baseClass} h-48 w-full`;
      case 'text':
        return `${baseClass} h-4`;
      case 'image':
        return `${baseClass} h-32 w-32 rounded-full`;
      case 'avatar':
        return `${baseClass} h-12 w-12 rounded-full`;
      case 'button':
        return `${baseClass} h-10 w-24`;
      case 'list':
        return `${baseClass} h-16 w-full`;
      case 'table':
        return `${baseClass} h-12 w-full`;
      case 'form':
        return `${baseClass} h-10 w-full`;
      default:
        return baseClass;
    }
  };

  if (type === 'text' && lines > 1) {
    return (
      <div className={`space-y-2 ${className}`}>
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={`${getSkeletonClass()} ${width || 'w-full'}`}
            style={{ height: height || '1rem' }}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={`${getSkeletonClass()} ${className}`}
      style={{ 
        width: width || undefined, 
        height: height || undefined 
      }}
    />
  );
};

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'secondary' | 'white' | 'gray';
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'primary',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const colorClasses = {
    primary: 'text-blue-600',
    secondary: 'text-gray-600',
    white: 'text-white',
    gray: 'text-gray-400'
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div
        className={`animate-spin rounded-full border-2 border-gray-300 border-t-transparent ${sizeClasses[size]} ${colorClasses[color]}`}
      />
    </div>
  );
};

interface LoadingDotsProps {
  className?: string;
}

export const LoadingDots: React.FC<LoadingDotsProps> = ({ className = '' }) => {
  return (
    <div className={`flex space-x-1 ${className}`}>
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
    </div>
  );
};

interface LoadingBarProps {
  progress?: number;
  showPercentage?: boolean;
  className?: string;
}

export const LoadingBar: React.FC<LoadingBarProps> = ({
  progress = 0,
  showPercentage = false,
  className = ''
}) => {
  return (
    <div className={`w-full ${className}`}>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>
      {showPercentage && (
        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 text-center">
          {Math.round(progress)}%
        </div>
      )}
    </div>
  );
};